import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { styles } from '../../../../styles/themes/style';
import {
  CAMPAIGN_TEMPLATE_VARIABLES,
  CampaignVariableKey,
  ICampaignVariableFallbacks,
  extractVariablesFromText,
  unrecognizedVariableTokens,
  variableToken,
} from '../../../../utils/campaignTemplateVariables';
import VariableFallbackPopover from './VariableFallbackPopover';
import { CampaignVariableWarnBanner } from './CampaignVariableWarnModal';
import CampaignComposeActions, { CampaignStepTools, UndoIcon } from './CampaignComposeActions';
import useReplaceUndo from './useReplaceUndo';
import CampaignGeneratePopup, { GenerateStepType } from './CampaignGeneratePopup';
import { LanguageStepType } from './CampaignLanguageDropdown';

/**
 * One field for writing a LinkedIn message, connection note or post comment.
 *
 * There is no AI/template switch any more. You either write the message or describe
 * what you want and press Generate; the draft comes back in a dialog and, if you
 * take it, it replaces what is in the field. From there it is ordinary text you can
 * keep editing — so what you read here is what every lead receives, with their own
 * {{variables}} filled in at send time.
 *
 * The rich-text sibling of this is CampaignEmailComposer. LinkedIn deliberately gets
 * no B/I/U: its messages are plain text, and formatting marks would either be
 * stripped or arrive as literal characters in the message.
 */

interface Props {
  label: string;
  required?: boolean;

  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  invalid?: boolean;

  stepType: GenerateStepType;
  languageStepType: LanguageStepType;
  language: string;
  onLanguageChange: (lang: string) => void;

  fallbacks: ICampaignVariableFallbacks;
  onFallbackChange: (key: CampaignVariableKey, value: string, saveAsUserDefault?: boolean) => void;

  campaignDescription?: string;
  /**
   * Who the preview is drawn as. No signature and no subject here — a LinkedIn
   * message has neither, and showing an email's chrome around one would misrepresent
   * what the recipient actually gets.
   */
  senderName?: string;
  generateLabel?: string;
  /** Hides the template picker where a library entry would not fit (short notes). */
  templateLibrary?: boolean;
  /**
   * The step is still stored in the old per-lead AI mode. Says so, because until it
   * is edited the text below is an instruction, not the message that goes out.
   */
  legacyAi?: boolean;
  hint?: React.ReactNode;
}

const CampaignContentField: React.FC<Props> = ({
  label, required, value, onChange, placeholder, rows = 5, maxLength, invalid,
  stepType, languageStepType, language, onLanguageChange,
  fallbacks, onFallbackChange, campaignDescription, senderName,
  generateLabel, templateLibrary = true, legacyAi, hint,
}) => {
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const [varOpen, setVarOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [popover, setPopover] = useState<{ key: CampaignVariableKey; anchorRect: DOMRect } | null>(null);

  const usedVariables = useMemo(() => extractVariablesFromText(value), [value]);
  const [badTokens, setBadTokens] = useState<string[]>([]);
  useEffect(() => {
    const timer = window.setTimeout(() => setBadTokens(unrecognizedVariableTokens(value)), 500);
    return () => window.clearTimeout(timer);
  }, [value]);
  const clamp = useCallback(
    (text: string) => (maxLength ? text.slice(0, maxLength) : text),
    [maxLength],
  );

  /*
    Templates and generated drafts overwrite the whole field, so they go through the
    undo helper rather than calling onChange directly. Typing does not — the browser
    already handles that, and routing it through here would fight with it.
  */
  const writeWhole = useCallback((text: string) => onChange(clamp(text)), [onChange, clamp]);
  const replaceUndo = useReplaceUndo(value, writeWhole);

  /** Insert at the caret, not at the end — appending lands the token after the sign-off. */
  const insertVariable = useCallback((key: CampaignVariableKey) => {
    setVarOpen(false);
    const token = variableToken(key);
    const el = areaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = clamp(`${value.slice(0, start)}${token}${value.slice(end)}`);
    onChange(next);
    requestAnimationFrame(() => {
      const caret = Math.min(start + token.length, next.length);
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }, [value, onChange, clamp]);

  return (
    <Wrap>
      <CampaignStepTools
        language={language}
        onLanguageChange={onLanguageChange}
        languageStepType={languageStepType}
        onPreview={() => setPreviewOpen(true)}
        previewDisabled={!value.trim()}
      />

      <Head>
        <Label>{label}{required && <Req> *</Req>}</Label>
        {maxLength && <Count $over={value.length > maxLength * 0.9}>{value.length}/{maxLength}</Count>}
      </Head>

      {legacyAi && (
        <LegacyNote>
          This step still writes a new message for every lead. Editing the text below,
          or taking a generated draft, switches it to one message for everyone.
        </LegacyNote>
      )}

      <Shell $invalid={invalid}>
        <Area
          ref={areaRef}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(clamp(e.target.value))}
          onKeyDown={replaceUndo.onKeyDown}
        />

        <Toolbar>
          <TBtn
            type="button"
            title="Clear"
            disabled={!value}
            onClick={() => { onChange(''); areaRef.current?.focus(); }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
              <path d="M4 7h16" /><path d="M10 7V5h4v2" /><path d="M6 7l1 13h10l1-13" />
            </svg>
          </TBtn>

          {/* Beside Clear, not down among the actions — both act on the text in the
              field, and undo is what takes the others back. */}
          <TBtn
            type="button"
            title="Undo — restore what was in the field (Ctrl+Z)"
            disabled={!replaceUndo.canUndo}
            onClick={replaceUndo.undo}
          >
            <UndoIcon />
          </TBtn>

          <Spacer />

          <VarButton type="button" onClick={() => setVarOpen(o => !o)}>
            Insert variable
            <Caret $open={varOpen}>
              <svg width="8" height="5" viewBox="0 0 8 5" fill="none">
                <path d="M1 1l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Caret>
            {varOpen && (
              <VarMenu onClick={e => e.stopPropagation()}>
                {CAMPAIGN_TEMPLATE_VARIABLES.map(v => (
                  <VarItem key={v.key} type="button" onClick={() => insertVariable(v.key)}>
                    {v.token}
                  </VarItem>
                ))}
              </VarMenu>
            )}
          </VarButton>
        </Toolbar>
      </Shell>

      <CampaignComposeActions
        generateLabel={generateLabel}
        generateDisabled={!value.trim()}
        onGenerate={() => setGenerateOpen(true)}
        onInsertTemplate={templateLibrary ? content => replaceUndo.replace(content) : undefined}
      />

      {hint && <Hint>{hint}</Hint>}

      <CampaignVariableWarnBanner tokens={badTokens} />

      {usedVariables.length > 0 && (
        <UsedRow>
          <UsedLabel>Fallback values:</UsedLabel>
          {usedVariables.map(key => {
            const has = !!(fallbacks[key] ?? '').trim();
            return (
              <UsedPill
                key={key}
                type="button"
                $missing={!has}
                onClick={e => setPopover({ key, anchorRect: e.currentTarget.getBoundingClientRect() })}
              >
                {variableToken(key)}{has && <FallbackHint>→ {fallbacks[key]}</FallbackHint>}
              </UsedPill>
            );
          })}
        </UsedRow>
      )}

      {/* One dialog, two modes — 'preview' renders what is in the field, 'generate'
          asks for a draft. Rendered separately so opening one cannot inherit the
          other's state. */}
      <CampaignGeneratePopup
        show={previewOpen}
        mode="preview"
        onHide={() => setPreviewOpen(false)}
        instruction={value}
        stepType={stepType}
        language={language}
        campaignDescription={campaignDescription}
        variableFallbacks={fallbacks}
        senderName={senderName}
        senderSubline={stepType === 'post_comment' ? 'LinkedIn comment' : 'LinkedIn'}
        onUse={() => { /* preview has nothing to accept */ }}
      />

      <CampaignGeneratePopup
        show={generateOpen}
        onHide={() => setGenerateOpen(false)}
        instruction={value}
        stepType={stepType}
        language={language}
        campaignDescription={campaignDescription}
        variableFallbacks={fallbacks}
        senderName={senderName}
        senderSubline={stepType === 'post_comment' ? 'LinkedIn comment' : 'LinkedIn'}
        onUse={text => replaceUndo.replace(text)}
      />

      {popover && (
        <VariableFallbackPopover
          variableKey={popover.key}
          value={fallbacks[popover.key] ?? ''}
          anchorRect={popover.anchorRect}
          mode="edit"
          onSave={(val, asDefault) => { onFallbackChange(popover.key, val, asDefault); setPopover(null); }}
          onClose={() => setPopover(null)}
        />
      )}

      {varOpen && <Scrim onClick={() => setVarOpen(false)} />}
    </Wrap>
  );
};

export default CampaignContentField;

// ─── Styles ───────────────────────────────────────────────────────────────────

const ACCENT = styles.colors.primary600;

const Wrap = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const Head = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
`;

const Label = styled.span`
  font-family: NunitoSans; font-size: 12px; font-weight: 700; color: #475569;
`;

const Req = styled.span`color: ${ACCENT};`;

const Count = styled.span<{ $over?: boolean }>`
  font-family: NunitoSans; font-size: 11px;
  color: ${p => (p.$over ? '#d97706' : '#94a3b8')};
`;

const LegacyNote = styled.div`
  border: 1px solid #fcd34d; background: #fffbeb; border-radius: 8px;
  padding: 8px 10px;
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #92400e;
`;

/* Field and toolbar share one border, so they read as a single control. */
const Shell = styled.div<{ $invalid?: boolean }>`
  border: 1.5px solid ${p => (p.$invalid ? '#fca5a5' : '#e2e8f0')};
  border-radius: 10px; background: #fff;
  overflow: visible;
  transition: border-color 0.12s;
  &:focus-within { border-color: ${ACCENT}; }
`;

const Area = styled.textarea`
  display: block; width: 100%; box-sizing: border-box;
  border: none; background: none; resize: vertical;
  padding: 12px 14px;
  font-family: NunitoSans, sans-serif; font-size: 13px; line-height: 1.6;
  color: ${styles.colors.black600};
  &:focus { outline: none; }
  &::placeholder { color: #b6bfcc; }
`;

const Toolbar = styled.div`
  display: flex; align-items: center; gap: 4px;
  padding: 6px 8px;
  border-top: 1px solid #eef1f6;
`;

const Spacer = styled.div`flex: 1 1 auto;`;

const TBtn = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 6px;
  border: none; background: none; cursor: pointer; color: #64748b;
  &:hover:not(:disabled) { background: #f1f5f9; color: ${ACCENT}; }
  &:disabled { opacity: .4; cursor: default; }
`;

const VarButton = styled.button`
  position: relative;
  display: inline-flex; align-items: center; gap: 5px;
  border: none; background: none; cursor: pointer;
  padding: 5px 7px; border-radius: 6px;
  font-family: NunitoSans; font-size: 12px; font-weight: 600; color: #64748b;
  white-space: nowrap;
  &:hover { background: #f1f5f9; color: ${ACCENT}; }
`;

const Caret = styled.span<{ $open?: boolean }>`
  display: flex; align-items: center;
  transform: ${p => (p.$open ? 'rotate(180deg)' : 'none')};
  transition: transform 0.15s;
  svg { display: block; }
`;

const VarMenu = styled.div`
  position: absolute; z-index: 60; right: 0; bottom: calc(100% + 6px);
  min-width: 170px; max-height: 240px; overflow-y: auto;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(20,30,80,0.12);
  padding: 5px;
`;

const VarItem = styled.button`
  display: block; width: 100%; text-align: left;
  padding: 7px 9px; border: none; background: none; border-radius: 6px; cursor: pointer;
  font-family: 'SF Mono', Menlo, monospace; font-size: 12px; color: ${ACCENT};
  &:hover { background: #eef2ff; }
`;

const Scrim = styled.div`
  position: fixed; inset: 0; z-index: 50;
`;

const Hint = styled.div`
  font-family: NunitoSans; font-size: 11px; line-height: 1.5; color: #94a3b8;
`;

const UsedRow = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
  padding-top: 8px; border-top: 1px dashed #e8ecf4;
`;

const UsedLabel = styled.span`
  font-family: NunitoSans; font-size: 11px; font-weight: 700; color: #64748b;
`;

const UsedPill = styled.button<{ $missing?: boolean }>`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px; border-radius: 8px;
  border: 1px solid ${p => (p.$missing ? '#fcd34d' : '#c7d2fe')};
  background: ${p => (p.$missing ? '#fffbeb' : '#eef2ff')};
  color: ${ACCENT};
  font-family: NunitoSans; font-size: 11px; font-weight: 700; cursor: pointer;
  &:hover { filter: brightness(0.98); }
`;

const FallbackHint = styled.span`
  font-weight: 500; color: #64748b; font-family: NunitoSans;
`;
